import type {Country} from '../models/country';

export type CountryOption = Readonly<{
  value: string;
  label: string;
}>;

// Country options only need these fields, so backend countries and lightweight fallbacks
// can use the same option-building code without requiring the full Country shape.
export type CountryOptionSource = Readonly<Pick<Country, 'code' | 'name' | 'nameEn'>>;

export const DEFAULT_COUNTRY_NAME = 'Deutschland';
export const FALLBACK_COUNTRIES: readonly CountryOptionSource[] = [
  {code: 'DE', name: 'Deutschland', nameEn: 'Germany'},
  {code: 'LU', name: 'Luxemburg', nameEn: 'Luxembourg'},
  {code: 'NL', name: 'Niederlande', nameEn: 'Netherlands'},
  {code: 'AT', name: 'Österreich', nameEn: 'Austria'},
  {code: 'CH', name: 'Schweiz', nameEn: 'Switzerland'},
];
export const COUNTRY_OPTIONS_STORAGE_KEY = 'direct-booking-countries';

const ENGLISH_LANGUAGE_PREFIX = 'en';

/**
 * Builds country options from backend countries.
 *
 * The persisted form value must stay stable across languages, so the German
 * backend `name` is always used as value. The label follows the active language.
 */
export function buildCountryOptions(
  countries: readonly CountryOptionSource[],
  language: string,
): CountryOption[] {
  const usesEnglishLabels = language.toLowerCase().startsWith(ENGLISH_LANGUAGE_PREFIX);

  return countries
    .filter((country) => country.name.trim().length > 0)
    .map((country) => ({
      value: country.name,
      label: usesEnglishLabels && country.nameEn.trim().length > 0 ? country.nameEn : country.name,
    }));
}

/**
 * Reads cached backend countries from browser storage.
 */
export function readStoredCountries(storage: Storage | null): CountryOptionSource[] {
  if (!storage) {
    return [];
  }

  const rawCountries = storage.getItem(COUNTRY_OPTIONS_STORAGE_KEY);

  if (!rawCountries) {
    return [];
  }

  try {
    return JSON.parse(rawCountries) as CountryOptionSource[];
  } catch {
    return [];
  }
}

/**
 * Stores backend countries for the current browser session.
 */
export function storeCountries(storage: Storage | null, countries: readonly Country[]): void {
  if (!storage) {
    return;
  }

  storage.setItem(COUNTRY_OPTIONS_STORAGE_KEY, JSON.stringify(countries));
}
